import { Clock, Circle } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import type { AgentSession } from '@/hooks/useAgentWebSocket'
import { useTranslation } from '@/i18n'

interface SessionListProps {
  sessions: AgentSession[]
  selectedId: string | null
  onSelect: (id: string) => void
  onAccept: (id: string) => void
}

export default function SessionList({ sessions, selectedId, onSelect, onAccept }: SessionListProps) {
  const { t, locale } = useTranslation()
  function tsOf(v?: string) {
    if (!v) return 0
    const t = new Date(v).getTime()
    return isNaN(t) ? 0 : t
  }

  function shortUserId(userId?: string) {
    if (!userId) return ''
    // Strip "codoon_" prefix for display
    const stripped = userId.startsWith('codoon_') ? userId.slice(7) : userId
    // Show first 8 chars
    return stripped.length > 12 ? stripped.slice(0, 8) + '…' : stripped
  }

  // Sort queued: newest queuedAt first (most recent escalation on top)
  const queued = sessions
    .filter(s => s.mode === 'queued')
    .slice()
    .sort((a, b) => tsOf(b.queuedAt) - tsOf(a.queuedAt))

  // Sort mine: most recent activity first (last message, else assigned)
  const mine = sessions
    .filter(s => s.mode === 'human')
    .slice()
    .sort((a, b) => {
      const ta = tsOf(a.lastMessageAt) || tsOf(a.assignedAt)
      const tb = tsOf(b.lastMessageAt) || tsOf(b.assignedAt)
      return tb - ta
    })

  function timeAgo(dateStr?: string) {
    if (!dateStr) return ''
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: locale === 'zh' ? zhCN : undefined })
    } catch {
      return ''
    }
  }

  function handleClick(session: AgentSession) {
    if (session.mode === 'queued') {
      onAccept(session.id)
    }
    onSelect(session.id)
  }

  return (
    <div className="w-72 border-r border-gray-200 flex flex-col bg-white h-full">
      <div className="flex-shrink-0">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('agent.pending')}</span>
          {queued.length > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">{queued.length}</span>
          )}
        </div>
        <div className="max-h-48 overflow-y-auto">
          {queued.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-400 text-center">{t('agent.noQueuedSessions')}</div>
          ) : (
            queued.map(session => (
              <button
                key={session.id}
                onClick={() => handleClick(session)}
                className={cn('w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-orange-50 transition-colors', selectedId === session.id && 'bg-orange-50')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Circle className="h-2.5 w-2.5 text-red-500 fill-red-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate flex-1">
                    {session.userId ? shortUserId(session.userId) : t('agent.newSession')}
                  </span>
                </div>
                <div className="flex items-center justify-between pl-4.5">
                  <span className="text-xs text-gray-400 truncate">{session.lastMessage || t('agent.unknownProject')}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                    <Clock className="h-3 w-3" />
                    {timeAgo(session.queuedAt)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('agent.myChats')}</span>
          <span className="text-xs text-gray-400">{mine.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {mine.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-400 text-center">{t('agent.noActiveChats')}</div>
          ) : (
            mine.map(session => (
              <button
                key={session.id}
                onClick={() => handleClick(session)}
                className={cn('w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors', selectedId === session.id && 'bg-indigo-50 border-l-2 border-l-indigo-500')}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Circle className="h-2.5 w-2.5 text-green-500 fill-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate flex-1">
                    {session.userId ? shortUserId(session.userId) : t('agent.chatting')}
                  </span>
                  {(session.unread || 0) > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-indigo-500 rounded-full px-1 flex-shrink-0">{session.unread}</span>
                  )}
                </div>
                <div className="flex items-center justify-between pl-4.5 gap-2">
                  <span className="text-xs text-gray-400 truncate flex-1">
                    {session.lastMessage || t('agent.chatting')}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(session.lastMessageAt || session.assignedAt)}</span>
                </div>
                {session.assignedAgentName && (
                  <div className="pl-4.5 mt-0.5">
                    <span className="text-[10px] text-indigo-500">接待: {session.assignedAgentName}</span>
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
